/**
 * Related Products - Performance Optimized
 */
!function() {
  "use strict";

  const SELECTORS = {
    API_TEMPLATE: "[data-api-related-template]",
    PRODUCT_ITEM: ".product-item",
    GRID_ITEM: "[data-grid-item]",
    RELATED_SECTION: "[data-related-section]",
    RECENTLY_VIEWED: "recently-viewed",
    GRID_SLIDER: "grid-slider",
    WRAPPER: "[data-recently-viewed-wrapper]",
    GRID_SLIDER_ATTR: "[data-grid-slider]"
  };

  const CLASSES = {
    HIDDEN: "hidden",
    MOBILE_SLIDER: "grid--mobile-slider",
    SLIDER: "grid--slider"
  };

  if (customElements.get("related-products")) return;

  customElements.define("related-products", class extends HTMLElement {
    connectedCallback() {
      // Cache DOM references once
      this._wrapper = this.querySelector(SELECTORS.WRAPPER);
      this._recentlyViewed = this.querySelector(SELECTORS.RECENTLY_VIEWED);
      this._relatedSection = this.querySelector(SELECTORS.RELATED_SECTION);
      this._relatedItems = 0;
      this._loaded = false;
      this._recentlyViewedHandled = false;

      // Use IntersectionObserver for lazy loading - 1200px margin for earlier loading
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(this._onIntersect.bind(this), {
          rootMargin: '1200px 0px',
          threshold: 0
        });
        this._observer.observe(this);
      } else {
        this._initProducts();
      }
    }

    disconnectedCallback() {
      this._cleanup();
    }

    _cleanup() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._mutationObserver) {
        this._mutationObserver.disconnect();
        this._mutationObserver = null;
      }
    }

    _onIntersect(entries) {
      if (entries[0].isIntersecting && !this._loaded) {
        this._loaded = true;
        this._observer.disconnect();
        this._initProducts();
      }
    }

    _initProducts() {
      // Load both in parallel
      this._loadRelatedProducts();
      this._loadRecentlyViewedProducts();
    }

    _loadRelatedProducts() {
      const section = this._relatedSection;
      if (!section) return;

      const productId = section.dataset.productId;
      const limit = section.dataset.limit;
      if (!productId) return;

      const url = `${window.theme.routes.product_recommendations_url}?section_id=api-product-recommendation&limit=${limit}&product_id=${productId}&intent=related`;

      fetch(url, { credentials: 'same-origin' })
        .then(res => res.ok ? res.text() : Promise.reject())
        .then(html => this._handleRelatedResponse(html, section))
        .catch(() => this._hideSection(section));
    }

    _handleRelatedResponse(html, section) {
      // Parse HTML efficiently
      const template = document.createElement("template");
      template.innerHTML = html;
      const apiTemplate = template.content.querySelector(SELECTORS.API_TEMPLATE);
      if (!apiTemplate) return;

      const items = apiTemplate.querySelectorAll(SELECTORS.GRID_ITEM);
      const itemCount = items.length;

      if (itemCount > 0) {
        section.innerHTML = apiTemplate.innerHTML;
        this._relatedItems = itemCount;

        const gridItem = section.querySelector(SELECTORS.GRID_ITEM);
        if (gridItem) {
          const parent = gridItem.parentElement;
          const mobileColumns = parseInt(section.style.getPropertyValue("--COLUMNS-MOBILE")) || 0;

          if (mobileColumns === 0) {
            parent.classList.add(CLASSES.MOBILE_SLIDER);
          }

          parent.classList.toggle(CLASSES.SLIDER, section.dataset.layout === "slider");
        }
      } else {
        this._hideSection(section);
      }

      this._updateVisibility();
    }

    _loadRecentlyViewedProducts() {
      const rv = this._recentlyViewed;
      if (!rv) return;

      // Check if already loaded
      if (rv.querySelectorAll(SELECTORS.PRODUCT_ITEM).length > 0) {
        this._handleRecentlyViewedResponse();
        return;
      }

      // Event listener
      rv.addEventListener("theme:recently-viewed:loaded", () => {
        this._handleRecentlyViewedResponse();
      }, { once: true });

      // MutationObserver fallback
      const contentWrapper = rv.querySelector(SELECTORS.GRID_SLIDER_ATTR);
      if (contentWrapper) {
        this._mutationObserver = new MutationObserver(() => {
          if (rv.querySelectorAll(SELECTORS.PRODUCT_ITEM).length > 0) {
            this._mutationObserver.disconnect();
            this._handleRecentlyViewedResponse();
          }
        });
        this._mutationObserver.observe(contentWrapper, { childList: true, subtree: true });
      }
    }

    _handleRecentlyViewedResponse() {
      if (this._recentlyViewedHandled) return;
      this._recentlyViewedHandled = true;

      const rv = this._recentlyViewed;
      const minimum = parseInt(rv.dataset.minimum) || 1;
      const itemCount = rv.querySelectorAll(SELECTORS.PRODUCT_ITEM).length;

      const shouldShow = (!this._wrapper && itemCount > 0) || (this._wrapper && itemCount >= minimum);

      if (shouldShow) {
        if (this._wrapper) {
          this._wrapper.classList.remove(CLASSES.HIDDEN);
        }
        rv.classList.remove(CLASSES.HIDDEN);
        rv.dispatchEvent(new CustomEvent("theme:tab:check", { bubbles: true }));

        const gridSlider = rv.querySelector(SELECTORS.GRID_SLIDER);
        if (gridSlider) {
          gridSlider.dispatchEvent(new CustomEvent("theme:grid-slider:init", { bubbles: true }));
        }
      }

      this._updateVisibility();
    }

    _hideSection(section) {
      section.dispatchEvent(new CustomEvent("theme:tab:hide", { bubbles: true }));
    }

    _updateVisibility() {
      const config = Shopify.Products?.getConfig?.() || {};
      const shouldHide = (config.howManyToShow || 0) < 1 && this._relatedItems < 1;
      this.classList.toggle(CLASSES.HIDDEN, shouldHide);
    }
  });
}();
